/**
 * Construction du payload d'order membership (single ou multi-participant).
 * Extrait du composant /a/[slug]/index.vue pour réduire la taille.
 */
import type {
  MembershipPlanPublic,
  MembershipPlanOption,
  CreateMembershipOrderPayload,
} from "~/types/membership";

interface ParticipantLocal {
  planId: string;
  planTitle: string;
  planPrice: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  selectedOptions: Record<string, boolean>;
  customFieldValues: Record<string, unknown>;
}

export interface UseMembershipOrderPayloadDeps {
  plan: Ref<MembershipPlanPublic | null>;
  participants: Ref<ParticipantLocal[]>;
  buyerInfo: Ref<{ firstName: string; lastName: string; email: string; phone: string }>;
  totalPrice: Ref<number>;
  includeFees: Ref<boolean>;
  donateUs: Ref<boolean>;
  donateToUsAmount: Ref<number>;
  /** Don à l'association, en EUROS (envoyé en centimes, migration 291). */
  donationAmount: Ref<number>;
  isFlexiblePricing: Ref<boolean>;
  flexiblePrice: Ref<number | null>;
  promoApplied: Ref<{ code: string } | null>;
  selectedProvider: Ref<"stripe" | "paypal">;
  getPlanOptions: (planId: string) => MembershipPlanOption[];
  /** Paiement en plusieurs fois : nombre de parts choisi (0 = en une fois). */
  nombreParts?: Ref<number>;
}

export function useMembershipOrderPayload(deps: UseMembershipOrderPayloadDeps) {
  /** Don en CENTIMES, absent sans don (le serveur le revalide). */
  function donCentimes(): number | undefined {
    const c = Math.round(Math.max(0, Number(deps.donationAmount.value) || 0) * 100);
    return c > 0 ? c : undefined;
  }

  /** Nombre de parts, absent en paiement en une fois (le serveur revalide). */
  function parts(): number | undefined {
    const n = Number(deps.nombreParts?.value) || 0;
    return n >= 2 ? n : undefined;
  }

  function buildOrderPayload(): CreateMembershipOrderPayload {
    const participantsData = deps.participants.value.map((p) => {
      const selectedOpts: Record<string, unknown> = {};
      const pOptions = deps.getPlanOptions(p.planId);
      for (const [optId, isSelected] of Object.entries(p.selectedOptions)) {
        if (isSelected) {
          const opt = pOptions.find((o) => o.id === optId);
          if (opt) selectedOpts[optId] = { name: opt.name, price: opt.price };
        }
      }
      return {
        plan_id: p.planId,
        member_name: `${p.firstName} ${p.lastName}`,
        member_email: p.email,
        member_phone: p.phone || undefined,
        selected_options: Object.keys(selectedOpts).length > 0 ? selectedOpts : undefined,
        custom_field_responses: Object.keys(p.customFieldValues).length > 0 ? p.customFieldValues : undefined,
      };
    });

    if (participantsData.length === 1) {
      const single = participantsData[0]!;
      return {
        plan_id: single.plan_id || deps.plan.value!.id,
        member_name: single.member_name,
        member_email: single.member_email,
        member_phone: single.member_phone,
        total_amount: deps.totalPrice.value,
        custom_price: deps.isFlexiblePricing.value ? Number(deps.flexiblePrice.value || 0) : undefined,
        selected_options: single.selected_options as Record<string, unknown> | undefined,
        custom_field_responses: single.custom_field_responses as Record<string, unknown> | undefined,
        include_fees: deps.includeFees.value,
        donate_to_us: deps.donateUs.value && deps.donateToUsAmount.value > 0 ? Math.round(deps.donateToUsAmount.value * 100) : undefined,
        promo_code: deps.promoApplied.value?.code || undefined,
        payment_provider: deps.selectedProvider.value,
        donation_amount: donCentimes(),
        installments: parts(),
      } as CreateMembershipOrderPayload;
    }

    return {
      plan_id: deps.plan.value!.id,
      member_name: `${deps.buyerInfo.value.firstName} ${deps.buyerInfo.value.lastName}`,
      member_email: deps.buyerInfo.value.email,
      member_phone: deps.buyerInfo.value.phone || undefined,
      total_amount: deps.totalPrice.value,
      include_fees: deps.includeFees.value,
      donate_to_us: deps.donateUs.value && deps.donateToUsAmount.value > 0 ? Math.round(deps.donateToUsAmount.value * 100) : undefined,
      participants: participantsData,
      promo_code: deps.promoApplied.value?.code || undefined,
      payment_provider: deps.selectedProvider.value,
      donation_amount: donCentimes(),
      installments: parts(),
    } as CreateMembershipOrderPayload;
  }

  return { buildOrderPayload };
}
